import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { Observable, concat, of } from 'rxjs';
import {
  CrewMemberResponse,
  TravelAssignmentResponse,
  UpdateTravelAssignmentCrewRequest,
} from '../../sdk/sdk';
import { CalendarService, CreateTravelAssignmentBody } from '../../services/calendar.service';
import { formatLocalIsoDate, formatUtcIsoDate, parseLocalIsoDate } from '../../utils/iso-date';
import {
  NewTravelLine,
  PickerPlacement,
  SavedTravelLine,
  TravelLineState,
  TravelLineStatus,
} from './travel-line.types';

type DateField = 'startDate' | 'endDate';
type EditableField = DateField | 'destinationBranchId';

interface TravelState {
  lines: TravelLineState[];
  eligibleCrew: Record<number, CrewMemberResponse[]>;
  eligibleCrewFetched: Record<number, boolean>;
  eligibleCrewLoading: Record<number, boolean>;
  openPickers: Record<string, boolean>;
  pickerPlacements: Record<string, PickerPlacement>;
  serverSnapshot: TravelAssignmentResponse[];
}

interface ErrorHandler {
  onError: (message: string) => void;
}

function noopErrorHandler(_message: string): void {
  return;
}

const EMPTY_STATE: TravelState = {
  lines: [],
  eligibleCrew: {},
  eligibleCrewFetched: {},
  eligibleCrewLoading: {},
  openPickers: {},
  pickerPlacements: {},
  serverSnapshot: [],
};

@Injectable()
export class TravelAssignmentsStore {
  private static readonly DATE_PICKER_HEIGHT_PX = 340;

  private readonly calendarService = inject(CalendarService);
  private readonly state = signal<TravelState>(EMPTY_STATE);
  private nextLineKey = 0;
  private errorHandler: ErrorHandler = { onError: noopErrorHandler };

  readonly lines: Signal<TravelLineState[]> = computed(() => this.state().lines);
  readonly errorMessage = signal('');
  readonly visibleLines: Signal<TravelLineState[]> = computed(() =>
    this.state().lines.filter((l) => l.status !== TravelLineStatus.Deleted),
  );
  readonly hasNoVisibleLines = computed(() => this.visibleLines().length === 0);

  static computePickerPlacement(inputs: {
    anchorTop: number;
    anchorBottom: number;
    viewportHeight: number;
    pickerHeight: number;
  }): PickerPlacement {
    const { anchorTop, anchorBottom, viewportHeight, pickerHeight } = inputs;
    const spaceBelow = viewportHeight - anchorBottom;
    const spaceAbove = anchorTop;
    if (spaceBelow >= pickerHeight) return 'below';
    if (spaceAbove >= pickerHeight) return 'above';
    return spaceAbove > spaceBelow ? 'above' : 'below';
  }

  configure(handler: ErrorHandler): void {
    this.errorHandler = handler;
  }

  loadFromServer(assignments: TravelAssignmentResponse[]): void {
    const todayIso = formatLocalIsoDate(new Date());
    const visible = assignments.filter((a) => formatUtcIsoDate(a.endDate) >= todayIso);

    const lineAndCrew =
      visible.length > 0
        ? visible.map((a) => this.toSavedLineWithCrew(a))
        : [{ line: this.createEmptyLine(), crew: [] as CrewMemberResponse[] }];

    const eligibleCrew: Record<number, CrewMemberResponse[]> = {};
    for (const { line, crew } of lineAndCrew) {
      if (crew.length > 0) eligibleCrew[line.lineKey] = crew;
    }

    this.state.set({
      ...EMPTY_STATE,
      lines: lineAndCrew.map((x) => x.line),
      eligibleCrew,
      serverSnapshot: assignments,
    });
  }

  reloadFromServer(foremanUserId: string): Observable<TravelAssignmentResponse[]> {
    return new Observable<TravelAssignmentResponse[]>((subscriber) => {
      const sub = this.calendarService.getTravelAssignments(foremanUserId).subscribe({
        next: (assignments) => {
          this.loadFromServer(assignments);
          subscriber.next(assignments);
          subscriber.complete();
        },
        error: (err: unknown) => subscriber.error(err),
      });
      return () => sub.unsubscribe();
    });
  }

  addLine(): void {
    this.state.update((s) => ({ ...s, lines: [...s.lines, this.createEmptyLine()] }));
  }

  removeLine(lineKey: number): void {
    this.state.update((s) => {
      const line = s.lines.find((l) => l.lineKey === lineKey);
      if (!line) return s;

      const eligibleCrew = { ...s.eligibleCrew };
      delete eligibleCrew[lineKey];
      const openPickers = { ...s.openPickers };
      const pickerPlacements = { ...s.pickerPlacements };
      for (const field of ['startDate', 'endDate'] as const) {
        delete openPickers[`${lineKey}-${field}`];
        delete pickerPlacements[`${lineKey}-${field}`];
      }

      if (line.status === TravelLineStatus.New) {
        return {
          ...s,
          lines: s.lines.filter((l) => l.lineKey !== lineKey),
          eligibleCrew,
          openPickers,
          pickerPlacements,
        };
      }

      return {
        ...s,
        lines: s.lines.map((l): TravelLineState =>
          l.lineKey === lineKey && l.status !== TravelLineStatus.New
            ? { ...l, status: TravelLineStatus.Deleted }
            : l,
        ),
        eligibleCrew,
        openPickers,
        pickerPlacements,
      };
    });
  }

  updateField(lineKey: number, field: EditableField, value: string): void {
    this.state.update((s) => ({
      ...s,
      lines: s.lines.map((l): TravelLineState =>
        l.lineKey === lineKey ? { ...l, [field]: value } : l,
      ),
    }));
  }

  updateCrew(lineKey: number, crewMemberIds: string[]): void {
    this.state.update((s) => ({
      ...s,
      lines: s.lines.map((l): TravelLineState => {
        if (l.lineKey !== lineKey) return l;
        if (l.status === TravelLineStatus.New) return { ...l, crewMemberIds };
        return { ...l, crewMemberIds, status: TravelLineStatus.Modified };
      }),
    }));
  }

  onCrewDropdownOpen(lineKey: number, foremanUserId: string): void {
    const s = this.state();
    if (s.eligibleCrewFetched[lineKey] || s.eligibleCrewLoading[lineKey]) return;
    this.fetchEligibleCrew(lineKey, foremanUserId);
  }

  fetchEligibleCrew(lineKey: number, foremanUserId: string): void {
    const line = this.state().lines.find((l) => l.lineKey === lineKey);
    if (!line?.startDate || !line?.endDate) return;

    const startDate = parseLocalIsoDate(line.startDate);
    const endDate = parseLocalIsoDate(line.endDate);
    if (!startDate || !endDate) return;

    this.state.update((s) => ({
      ...s,
      eligibleCrewLoading: { ...s.eligibleCrewLoading, [lineKey]: true },
    }));

    this.calendarService.getEligibleCrew(foremanUserId, startDate, endDate).subscribe({
      next: (crew) => this.applyEligibleCrew(lineKey, crew),
      error: (err: unknown) => this.handleEligibleCrewError(lineKey, err),
    });
  }

  handleDate(lineKey: number, field: DateField, date: Date | null, foremanUserId: string): void {
    if (!date) return;
    const iso = formatLocalIsoDate(date);
    this.updateField(lineKey, field, iso);

    if (field === 'startDate') {
      const line = this.state().lines.find((l) => l.lineKey === lineKey);
      if (line?.endDate && line.endDate < iso) {
        this.updateField(lineKey, 'endDate', '');
      }
    }

    this.toggleDatePicker(`${lineKey}-${field}`);
    this.fetchEligibleCrew(lineKey, foremanUserId);
  }

  toggleDatePicker(key: string, anchor?: HTMLElement): void {
    this.state.update((s) => {
      const isOpening = !s.openPickers[key];
      const placement = isOpening
        ? this.resolvePickerPlacement(anchor)
        : s.pickerPlacements[key];
      return {
        ...s,
        openPickers: { ...s.openPickers, [key]: isOpening },
        pickerPlacements: { ...s.pickerPlacements, [key]: placement ?? 'below' },
      };
    });
  }

  pickerPlacement(key: string): PickerPlacement {
    return this.state().pickerPlacements[key] ?? 'below';
  }

  isPickerOpen(key: string): boolean {
    return this.state().openPickers[key] ?? false;
  }

  getEligibleCrewForLine(lineKey: number): CrewMemberResponse[] {
    return this.state().eligibleCrew[lineKey] ?? [];
  }

  isCrewLoadingFor(lineKey: number): boolean {
    return this.state().eligibleCrewLoading[lineKey] ?? false;
  }

  isLastVisibleLine(lineKey: number): boolean {
    const lines = this.state().lines;
    for (let i = lines.length - 1; i >= 0; i--) {
      if (lines[i].status !== TravelLineStatus.Deleted) {
        return lines[i].lineKey === lineKey;
      }
    }
    return false;
  }

  validate(): string | null {
    const partial = this.nonEmptyNewLines().find(
      (l) => !l.startDate || !l.endDate || !l.destinationBranchId,
    );
    if (partial) {
      return 'All travel lines must have start date, end date, and destination branch.';
    }
    if (this.findOverlappingLines()) {
      return 'Travel line dates overlap — a foreman cannot be assigned to travel on overlapping date ranges.';
    }
    return null;
  }

  saveChanges(foremanUserId: string): Observable<unknown> {
    const lines = this.state().lines;
    const deletes = lines
      .filter((l): l is SavedTravelLine => l.status === TravelLineStatus.Deleted)
      .map((l) => this.calendarService.deleteTravelAssignment(l.id));

    const creates = this.nonEmptyNewLines().map((l) =>
      this.calendarService.createTravelAssignment(foremanUserId, this.toCreateBody(l)),
    );

    const updates = lines
      .filter((l): l is SavedTravelLine => l.status === TravelLineStatus.Modified)
      .map((l) =>
        this.calendarService.updateTravelAssignmentCrew(
          l.id,
          new UpdateTravelAssignmentCrewRequest({ crewMemberUserIds: l.crewMemberIds }),
        ),
      );

    const ops = [...deletes, ...creates, ...updates];
    if (ops.length === 0) return of(undefined);
    return concat(...ops);
  }

  private applyEligibleCrew(lineKey: number, crew: CrewMemberResponse[]): void {
    this.state.update((s) => {
      const existing = s.eligibleCrew[lineKey] ?? [];
      const fetchedIds = new Set(crew.map((c) => c.userId));
      const preserved = existing.filter((e) => !fetchedIds.has(e.userId));
      return {
        ...s,
        eligibleCrew: { ...s.eligibleCrew, [lineKey]: [...crew, ...preserved] },
        eligibleCrewFetched: { ...s.eligibleCrewFetched, [lineKey]: true },
        eligibleCrewLoading: { ...s.eligibleCrewLoading, [lineKey]: false },
      };
    });

    const currentLine = this.state().lines.find((l) => l.lineKey === lineKey);
    if (currentLine?.status === TravelLineStatus.New) {
      const eligibleIds = new Set(crew.map((c) => c.userId));
      const filtered = currentLine.crewMemberIds.filter((id) => eligibleIds.has(id));
      if (filtered.length !== currentLine.crewMemberIds.length) {
        this.updateCrew(lineKey, filtered);
      }
    }
  }

  private handleEligibleCrewError(lineKey: number, err: unknown): void {
    console.error('Failed to fetch eligible crew', { lineKey, err });
    this.state.update((s) => ({
      ...s,
      eligibleCrewLoading: { ...s.eligibleCrewLoading, [lineKey]: false },
      lines: s.lines.map((l): TravelLineState => {
        if (l.lineKey !== lineKey) return l;
        if (l.status === TravelLineStatus.New) return { ...l, crewMemberIds: [] };
        return { ...l, crewMemberIds: [], status: TravelLineStatus.Modified };
      }),
    }));
    const message =
      'Could not load eligible crew for this travel line. Crew selection has been cleared — please reopen the dropdown to try again.';
    this.errorMessage.set(message);
    this.errorHandler.onError(message);
  }

  private findOverlappingLines(): boolean {
    const active = this.state()
      .lines.filter((l) => l.status !== TravelLineStatus.Deleted)
      .filter((l) => l.startDate && l.endDate);
    for (let i = 0; i < active.length; i++) {
      for (let j = i + 1; j < active.length; j++) {
        const a = active[i];
        const b = active[j];
        if (a.startDate <= b.endDate && b.startDate <= a.endDate) return true;
      }
    }
    return false;
  }

  private nonEmptyNewLines(): NewTravelLine[] {
    return this.state()
      .lines.filter((l): l is NewTravelLine => l.status === TravelLineStatus.New)
      .filter(
        (l) =>
          l.startDate ||
          l.endDate ||
          l.destinationBranchId ||
          l.crewMemberIds.length > 0,
      );
  }

  private toSavedLineWithCrew(a: TravelAssignmentResponse): {
    line: SavedTravelLine;
    crew: CrewMemberResponse[];
  } {
    const line: SavedTravelLine = {
      lineKey: this.nextLineKey++,
      id: a.id,
      startDate: formatUtcIsoDate(a.startDate),
      endDate: formatUtcIsoDate(a.endDate),
      destinationBranchId: a.destinationBranchId,
      crewMemberIds: (a.crewMembers ?? []).map((c) => c.userId!),
      status: TravelLineStatus.Unchanged,
    };
    return { line, crew: a.crewMembers ?? [] };
  }

  private createEmptyLine(): NewTravelLine {
    return {
      lineKey: this.nextLineKey++,
      startDate: '',
      endDate: '',
      destinationBranchId: '',
      crewMemberIds: [],
      status: TravelLineStatus.New,
    };
  }

  private toCreateBody(l: NewTravelLine): CreateTravelAssignmentBody {
    return {
      startDate: l.startDate,
      endDate: l.endDate,
      destinationBranchId: l.destinationBranchId,
      crewMemberUserIds: l.crewMemberIds.length > 0 ? l.crewMemberIds : undefined,
    };
  }

  private resolvePickerPlacement(anchor?: HTMLElement): PickerPlacement {
    if (!anchor) return 'below';
    const rect = anchor.getBoundingClientRect();
    return TravelAssignmentsStore.computePickerPlacement({
      anchorTop: rect.top,
      anchorBottom: rect.bottom,
      viewportHeight: window.innerHeight,
      pickerHeight: TravelAssignmentsStore.DATE_PICKER_HEIGHT_PX,
    });
  }
}
